import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import { getP2pSettings, updateP2pSettings } from '../p2p/settingsApi';

// Platform-wide rules for the OTC (cash in person) section. Stored alongside
// the P2P settings in the p2p service but managed from its own page.
const OtcSettingsPage: React.FC = () => {
  const { admin } = useAuth();
  const canManage =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('p2p.manage_settings') ?? false);

  const [minLevel, setMinLevel] = useState('2');
  const [maxFiat, setMaxFiat] = useState('');
  const [timeLimit, setTimeLimit] = useState('180');
  const [cashEnabled, setCashEnabled] = useState(true);
  const [cashMinSamples, setCashMinSamples] = useState('5');
  const [cashSellerChecks, setCashSellerChecks] = useState('3');
  const [cashMinFiat, setCashMinFiat] = useState('');
  const [cashBlocksRelease, setCashBlocksRelease] = useState(true);
  const [cashStrictFormat, setCashStrictFormat] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getP2pSettings();
        if (cancelled) return;
        setMinLevel(String(s.otc_min_verification_level ?? 2));
        setMaxFiat(s.otc_max_fiat_amount ? String(s.otc_max_fiat_amount) : '');
        setTimeLimit(String(s.otc_default_time_limit ?? 180));
        setCashEnabled(s.otc_cash_verification_enabled ?? true);
        setCashMinSamples(String(s.otc_cash_min_samples ?? 5));
        setCashSellerChecks(String(s.otc_cash_seller_checks ?? 3));
        setCashMinFiat(s.otc_cash_min_fiat_amount ? String(s.otc_cash_min_fiat_amount) : '');
        setCashBlocksRelease(s.otc_cash_release_requires_check ?? true);
        setCashStrictFormat(s.otc_cash_strict_serial_format ?? true);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err, 'Failed to load OTC settings'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);

    const level = Number(minLevel);
    const limit = Number(timeLimit);
    const cap = maxFiat.trim();
    if (!Number.isInteger(level) || level < 0 || level > 5) {
      setError('Verification level must be between 0 and 5');
      return;
    }
    if (!Number.isInteger(limit) || limit < 5 || limit > 1440) {
      setError('Meeting window must be between 5 and 1440 minutes');
      return;
    }
    if (cap !== '' && (Number.isNaN(Number(cap)) || Number(cap) < 0)) {
      setError('Max fiat amount must be a non-negative number');
      return;
    }
    const samples = Number(cashMinSamples);
    const checks = Number(cashSellerChecks);
    const cashFloor = cashMinFiat.trim();
    if (!Number.isInteger(samples) || samples < 1 || samples > 100) {
      setError('Buyer banknote samples must be between 1 and 100');
      return;
    }
    if (!Number.isInteger(checks) || checks < 1 || checks > 100) {
      setError('Seller checks must be between 1 and 100');
      return;
    }
    if (checks > samples) {
      setError('Seller checks cannot exceed the number of buyer samples');
      return;
    }
    if (cashFloor !== '' && (Number.isNaN(Number(cashFloor)) || Number(cashFloor) < 0)) {
      setError('Cash verification threshold must be a non-negative number');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateP2pSettings({
        otc_min_verification_level: level,
        otc_max_fiat_amount: cap === '' ? null : cap,
        otc_default_time_limit: limit,
        otc_cash_verification_enabled: cashEnabled,
        otc_cash_min_samples: samples,
        otc_cash_seller_checks: checks,
        otc_cash_min_fiat_amount: cashFloor === '' ? null : cashFloor,
        otc_cash_release_requires_check: cashBlocksRelease,
        otc_cash_strict_serial_format: cashStrictFormat,
      });
      setBanner('OTC settings saved');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save OTC settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">OTC Limits</h1>
          <p className="text-slate-400 mt-1">
            Platform-wide rules for OTC trades — crypto sold for cash at an in-person meeting
          </p>
        </div>

        <div className="card p-6 max-w-2xl">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Loading…
            </div>
          ) : (
            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Minimum verification level</label>
                <p className="text-xs text-slate-500 mb-2">
                  Required to create OTC offers and to open OTC trades. Offers cannot demand a lower level than this.
                </p>
                <input
                  type="number" min={0} max={5} step={1}
                  value={minLevel}
                  onChange={(e) => { setBanner(null); setMinLevel(e.target.value); }}
                  className="input w-40"
                  disabled={!canManage}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Max fiat amount per trade</label>
                <p className="text-xs text-slate-500 mb-2">
                  Hard cap on a single OTC trade in the offer&apos;s fiat currency. Leave empty for no cap.
                  Keep in mind physical-meeting risk and local cash-exchange regulations.
                </p>
                <input
                  type="number" min={0} step="0.01"
                  value={maxFiat}
                  onChange={(e) => { setBanner(null); setMaxFiat(e.target.value); }}
                  className="input w-60"
                  placeholder="no limit"
                  disabled={!canManage}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Default meeting window (minutes)</label>
                <p className="text-xs text-slate-500 mb-2">
                  Pre-filled time limit for new OTC offers — how long the buyer has to meet and hand over the cash
                  before the trade auto-cancels. 5–1440 minutes.
                </p>
                <input
                  type="number" min={5} max={1440} step={1}
                  value={timeLimit}
                  onChange={(e) => { setBanner(null); setTimeLimit(e.target.value); }}
                  className="input w-40"
                  disabled={!canManage}
                />
              </div>

              <div className="border-t border-slate-800 pt-5 space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-slate-200">Banknote verification</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Before the meeting the seller scans the serial numbers of banknotes they will bring (the serial is
                    read on the server); at the meeting the buyer must scan a random subset of them to make sure they
                    are dealing with the real seller before handing over the cash. Limits apply to trades created
                    after saving — in-flight orders keep the values they were created with.
                  </p>
                </div>

                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={cashEnabled}
                    onChange={(e) => { setBanner(null); setCashEnabled(e.target.checked); }}
                    disabled={!canManage}
                    className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500"
                  />
                  Require banknote verification for OTC trades
                </label>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Seller must register (banknotes)</label>
                  <p className="text-xs text-slate-500 mb-2">
                    Number of distinct banknotes the seller registers before the meeting and brings along to be
                    identified by the buyer.
                  </p>
                  <input
                    type="number" min={1} max={100} step={1}
                    value={cashMinSamples}
                    onChange={(e) => { setBanner(null); setCashMinSamples(e.target.value); }}
                    className="input w-40"
                    disabled={!canManage || !cashEnabled}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Buyer must verify (banknotes)</label>
                  <p className="text-xs text-slate-500 mb-2">
                    How many of the registered banknotes are randomly picked for the buyer to scan at the meeting.
                    Cannot exceed the number the seller registers.
                  </p>
                  <input
                    type="number" min={1} max={100} step={1}
                    value={cashSellerChecks}
                    onChange={(e) => { setBanner(null); setCashSellerChecks(e.target.value); }}
                    className="input w-40"
                    disabled={!canManage || !cashEnabled}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Only for trades from (fiat amount)</label>
                  <p className="text-xs text-slate-500 mb-2">
                    Trades below this fiat total skip banknote verification. Leave empty to verify every OTC trade.
                  </p>
                  <input
                    type="number" min={0} step="0.01"
                    value={cashMinFiat}
                    onChange={(e) => { setBanner(null); setCashMinFiat(e.target.value); }}
                    className="input w-60"
                    placeholder="every trade"
                    disabled={!canManage || !cashEnabled}
                  />
                </div>

                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={cashBlocksRelease}
                    onChange={(e) => { setBanner(null); setCashBlocksRelease(e.target.checked); }}
                    disabled={!canManage || !cashEnabled}
                    className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500"
                  />
                  Block the cash handover and escrow release until the buyer has verified the seller's banknotes
                </label>

                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={cashStrictFormat}
                    onChange={(e) => { setBanner(null); setCashStrictFormat(e.target.checked); }}
                    disabled={!canManage || !cashEnabled}
                    className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500"
                  />
                  <span>
                    Strict serial format per fiat currency
                    <span className="block text-xs text-slate-500">
                      On: a USD trade accepts only USD-shaped serials (PF34821975A), INR only 3AB123456, etc.
                      Off: any banknote serial is accepted — use for testing with other currencies.
                    </span>
                  </span>
                </label>
              </div>

              <div className="border-t border-slate-800 pt-4 text-xs text-slate-500 space-y-1">
                <p>
                  Fee schedules, market restrictions and the holding period for OTC are managed on their own pages:{' '}
                  <Link to="/otc/rates" className="text-indigo-400 hover:underline">Fee Rates</Link>,{' '}
                  <Link to="/otc/restrictions" className="text-indigo-400 hover:underline">Restrictions</Link>,{' '}
                  <Link to="/otc/holding-period" className="text-indigo-400 hover:underline">Holding Period</Link>.
                  The dispute window is managed from{' '}
                  <Link to="/otc/disputes" className="text-indigo-400 hover:underline">OTC Disputes → Dispute settings</Link>.
                  OTC offers are always settled in cash, so there is no payment-method catalog for this section.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
              )}
              {banner && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">{banner}</div>
              )}

              {canManage && (
                <div className="flex justify-end">
                  <button type="submit" disabled={saving} className="btn btn-primary py-2">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default OtcSettingsPage;
